<template>
  <div class="max-w-[1440px]">
    <span class="text-base text-stronger font-semibold font-inter">Need help?</span>
    <h1 class="text-4xl text-strongest font-semibold mb-4">Contact Us</h1>
    <div v-if="formSubmitted" class="w-full bg-success-bg rounded-2xl mb-6 mt-5">
      <img
        v-if="UIStore.is_dark_mode"
        src="@/assets/images/il-contact-submitted.webp"
        alt="Contact Submitted"
        class="w-24 h-24"
      />
      <img
        v-else
        src="@/assets/images/il-contact-submitted-light.webp"
        alt="Contact Submitted"
        class="w-24 h-24"
      />
      <p class="text-strongest text-2xl font-semibold">Your response has been received.</p>
      <p class="text-light text-xs font-inter font-medium">
        Our team will get back to you with a response for your query shortly.
      </p>
      <div class="flex items-center gap-2 mt-6">
        <Button variant="primary-paper" class="mt-4 px-6" @click="router.push('/dashboard')">
          <template #label>
            <span class="font-inter">Back to Dashboard</span>
          </template>
        </Button>
        <Button variant="paper-primary" class="mt-4 px-6" @click="formSubmitted = false">
          <template #label>
            <span class="font-inter">Submit another query</span>
          </template>
        </Button>
      </div>
    </div>
    <div v-else>
      <Form
        :schema_array="contactUsFields"
        :state_object="stateObject"
        :record_id="dataStore.contact.record_id"
        :variant="'secondary'"
        :class="'mt-5 md:mt-60'"
        :dropdown_above="false"
        :errors="{
          help_type: help_type_invalid ? 'Please select an option' : '',
        }"
      />
      <!-- <p
        class="h-6 flex font-inter-tight text-xs font-medium text-left text-light items-start mt-2"
      >
        <Icon
          name="Asterisk"
          class="mr-1 h-[14px] min-h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-light"
        />
        Accredited investors as defined in Rule 501 of Regulation D includes individuals with a net
        worth over $1 million (excluding primary residence) or income over $200,000 (individual) or
        $300,000 (household) in each of the prior two years, with a reasonable expectation of the
        same in the current year. Learn more about accreditation requirements here. Please refer to
        the Company’s filings with the SEC.
      </p> -->
      <div
        class="w-full bg-container-bg rounded-2xl pt-4 px-4 mt-4 font-intertight h-60 text-strong text-lg"
      >
        <span
          class="pointer-events-none z-10 font-inter text-sm md:text-base text-stronger opacity-70 mb-[2px]"
          >Comment or Message</span
        >
        <textarea
          ref="input"
          v-model="stateObject.message"
          class="w-full bg-container-bg z-0 focus:outline-none focus:ring-0 text-base md:text-lg font-inter text-stronger leading-[1.2rem] h-30 resize-none"
          placeholder="Your Message"
        />
      </div>
      <div class="w-full flex justify-end">
        <Button variant="primary" class="mt-4 px-24" @click="submitForm()">
          <template #label>
            <span class="font-inter">Submit</span>
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
// script for the investor dashboard
import { ref, onMounted, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Button, Icon, BaseInput, Form } from "@/components";
import { useUIStore, useDataStore, useInvestmentStore, useAuthStore, useCrudStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";
import { drop } from "lodash";

const router = useRouter();
const route = useRoute();
const UIStore = useUIStore();
const dataStore = useDataStore();
const authStore = useAuthStore();
const investmentStore = useInvestmentStore();
const crudStore = useCrudStore();
const { contactFormSubmitted } = useAnalytics();

const help_type_invalid = ref(false);

const contactUsFields = [
  {
    field_name: "first_name",
    label: "First Name",
    field_type: "string",
    read_only: dataStore.contact.first_name ? true : false,
    is_required: true,
  },
  {
    field_name: "last_name",
    label: "Last Name",
    field_type: "string",
    read_only: dataStore.contact.last_name ? true : false,
    is_required: true,
  },
  {
    field_name: "email",
    label: "Email",
    field_type: "string",
    read_only: dataStore.contact.email ? true : false,
    is_required: true,
  },
  {
    field_name: "phone",
    label: "Phone",
    field_type: "string",
    read_only: dataStore.contact.phone ? true : false,
    is_required: true,
  },
  // {
  //   field_name: "accredited_investor",
  //   label: "Are you an Accredited Investor",
  //   field_type: "dropdown",
  //   dropdown_options: [
  //     "Net worth of +$1M excluding primary residence",
  //     "I've earned +$200K for each of the past 2 years",
  //     "I've earned +$300K each of the past 2 yrs w/spouse",
  //     "I have a net worth of $250K but less than $1M",
  //     "I have both a gross income and liquid net worth of $70K",
  //     "Not sure, I am new to investing",
  //   ],
  //   is_required: true,
  //   error_message: accredited_invalid.value ? "Please select an option" : "",
  // },
  {
    field_name: "help_type",
    label: "What do you need help with?",
    field_type: "dropdown",
    dropdown_options: [
      "Learn about higher available rates for accredited investors",
      "Account Access & Portal Support",
      "Tax Documentation & Forms",
      "Investment & Transaction Inquiry",
      "General Information/New Inquiries",
      "Technical Reporting Error",
      "Accreditation Status (Questions about qualifying or verifying)",
    ],
    is_required: true,
  },
];

const formSubmitted = ref(false);

const stateObject = ref({
  first_name: dataStore.contact.first_name,
  last_name: dataStore.contact.last_name,
  email: dataStore.contact.email,
  phone: dataStore.contact.phone,
  help_type: "Select an option",
  message: "",
  contact_id: dataStore.contact.record_id,
});

onMounted(async () => {
  window.scrollTo(0, 0);
});

async function submitForm() {
  if (stateObject.value.help_type === "Select an option") {
    help_type_invalid.value = true;
  } else {
    help_type_invalid.value = false;
  }

  let validation_details = null;
  if (help_type_invalid.value) {
    validation_details = {
      invalid_fields: ["help_type"],
      error_messages: [{ field_name: "help_type", message: "Please select an option." }],
      field_errors: { help_type: "Please select an option." },
    };
  }

  let message = JSON.stringify({
    first_name: stateObject.value.first_name,
    last_name: stateObject.value.last_name,
    email: stateObject.value.email,
    phone: stateObject.value.phone,
    help_type: stateObject.value.help_type,
    message: stateObject.value.message,
  });

  if (validation_details) {
    message = "Validation Failed";
  }

  let status = validation_details ? "Validation Failed" : "Submitted";
  // log to investor activity
  let log = {
    step: "Contact Us Form",
    substep: "Contact Form",
    status: status,
    message: message,
    url: route.fullPath,
    validation_details: validation_details,
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);

  if (help_type_invalid.value) {
    return;
  }

  // Send Email
  const SUPABASE_URL = import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT;
  await fetch(`${SUPABASE_URL}/send-contact-us-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      user_id: dataStore.contact.supabase_user_id,
      session_id: authStore.current_user.session_id,
    },
    body: JSON.stringify({ formFields: stateObject.value }),
  });
  contactFormSubmitted(stateObject.value.help_type);
  // Clear Form
  stateObject.value = {
    first_name: dataStore.contact.first_name,
    last_name: dataStore.contact.last_name,
    email: dataStore.contact.email,
    phone: dataStore.contact.phone,
    help_type: "Select an option",
    message: "",
    contact_id: dataStore.contact.record_id,
  };
  formSubmitted.value = true;
}
</script>
